import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import Select from "../../Common/FormElements/Select/Select";
import CalendarInput from "../../Common/FormElements/CalendarInput/CalendarInput";
import TextArea from "../../Common/FormElements/TextArea/TextArea";
import Button from "../../Common/FormElements/Button/Button";
import ConfirmModal from "../../Common/Modal/ConfirmModal";
import QuotePill from "./QuotePill";
import {
  markSaleDelivered,
  proposeSaleDelivery,
  validateSaleDelivery,
} from "../../../REDUX/actions/appProjects";
import { openCenteredModal } from "../../../REDUX/actions/centeredModal";

// Why the store proposes another date (API delivery/utils/deliveryDate).
const PROPOSAL_REASONS = [
  { code: "supplier_delay", name: "Supplier delay" },
  { code: "transport", name: "Carrier" },
  { code: "other", name: "Other" },
];
const SLOTS = { morning: "Morning", afternoon: "Afternoon" };
const day = (value) => moment(value).format("DD/MM/YYYY");

/**
 * Client sheet › Livraison: the store delivers its customer (not a Poseidon
 * delivery). The customer asks for a date in the app; the store validates it
 * or proposes another one (the customer accepts or counter-proposes), then
 * marks the order as delivered.
 */
const ProjectDelivery = ({ sale, projectId, onChange }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const [proposal, setProposal] = useState({});
  const [saving, setSaving] = useState(false);
  const delivery = sale.delivery ?? {};
  const customer = sale.customer?.name ?? t("Client");
  const closed = sale.state === "cancelled" || sale.state === "delivered";

  const run = async (action) => {
    setSaving(true);
    const data = await dispatch(action);
    setSaving(false);
    if (data) onChange(data);
    return data;
  };

  const confirm = (text, action) =>
    dispatch(
      openCenteredModal(
        <ConfirmModal text={text} confirmText={t("Confirm")} onConfirm={() => run(action)} />,
      ),
    );

  const reasons = PROPOSAL_REASONS.map(({ code, name }) => ({ code, name: t(name) }));
  const reasonOf = (code) => reasons.find((reason) => reason.code === code)?.name;
  const validatedBy = [delivery.validatedBy?.firstName, delivery.validatedBy?.lastName]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="px-5 py-4 d-flex flex-column gap-4">
      <div className="p-3 br10 b-1-lightGrey">
        <p className="f14 sBold text-ultraBlack mb-2">{t("Delivery date")}</p>
        {delivery.date ? (
          <div className="d-flex align-items-center flex-wrap gap-2">
            <span className="f16 sBold">
              {day(delivery.date)}
              {delivery.slot ? ` — ${t(SLOTS[delivery.slot])}` : ""}
            </span>
            {delivery.source === "app" && (
              <QuotePill tone="chip">{t("Requested from the app")}</QuotePill>
            )}
            {delivery.validated ? (
              <QuotePill tone="green">
                {validatedBy
                  ? t("validated by {{name}}", { name: validatedBy })
                  : t("Validated")}
              </QuotePill>
            ) : (
              <QuotePill tone="orange">{t("To validate")}</QuotePill>
            )}
          </div>
        ) : (
          <p className="f14 text-brightGrey mb-0">
            {closed
              ? "-"
              : t(
                  "No date yet: {{name}} asks for one in the app, or propose one below.",
                  { name: customer },
                )}
          </p>
        )}
        {delivery.proposedDate && (
          <p className="f14 mt-2 mb-0">
            {t("Date proposed: {{date}} ({{reason}}) — waiting for {{name}}'s answer", {
              date: day(delivery.proposedDate),
              reason: reasonOf(delivery.proposedReason) ?? "-",
              name: customer,
            })}
          </p>
        )}
        {delivery.date && !delivery.validated && !closed && (
          <Button
            className="mt-3"
            loading={saving}
            onClick={() =>
              confirm(
                t("{{name}} will be notified. Validate the delivery date of {{date}}", {
                  date: day(delivery.date),
                  name: customer,
                }),
                validateSaleDelivery(sale.id, t),
              )
            }
          >
            {t("Validate the date")}
          </Button>
        )}
      </div>

      {!closed && (
        <div className="p-3 br10 b-1-lightGrey d-flex flex-column gap-2">
          <p className="f14 sBold text-ultraBlack mb-1">
            {t("Propose a new date to the customer")}
          </p>
          <div className="d-flex align-items-end gap-3 flex-wrap">
            <CalendarInput
              placeholder={t("Delivery date")}
              value={proposal.date ?? null}
              callback={(e) => setProposal((prev) => ({ ...prev, date: e.value ?? null }))}
            />
            <div style={{ width: 220 }}>
              <Select
                appendToSelf
                placeholder={t("Reason")}
                options={reasons}
                value={proposal.reason ?? null}
                callback={(e) => setProposal((prev) => ({ ...prev, reason: e.value ?? null }))}
              />
            </div>
            <Button
              loading={saving}
              disabled={!proposal.date || !proposal.reason}
              onClick={async () => {
                if (!proposal.date || !proposal.reason) return;
                const data = await run(
                  proposeSaleDelivery(
                    sale.id,
                    {
                      proposedDate: moment(proposal.date).format("YYYY-MM-DD"),
                      proposedReason: proposal.reason.code,
                      note: proposal.note,
                    },
                    t,
                  ),
                );
                if (data) setProposal({});
              }}
            >
              {t("Save and notify the customer")}
            </Button>
          </div>
          <TextArea
            placeholder={t("Comment")}
            value={proposal.note ?? ""}
            callback={(e) => setProposal((prev) => ({ ...prev, note: e.target.value }))}
          />
          <p className="f13 text-brightGrey mb-0">
            {t(
              "{{name}} will be notified of the new date and can accept it or propose another one from the app.",
              { name: customer },
            )}
          </p>
        </div>
      )}

      <div className="p-3 br10 b-1-lightGrey">
        <p className="f14 sBold text-ultraBlack mb-2">{t("Products to deliver")}</p>
        {(sale.units ?? []).map((unit) => (
          <p className="f14 mb-1" key={unit.id}>
            {unit.model ?? "-"}
            {unit.serialNumber ? ` — SN ${unit.serialNumber}` : ""}
          </p>
        ))}
        {!(sale.units ?? []).length && <p className="f14 text-brightGrey mb-0">-</p>}
      </div>

      <div className="d-flex align-items-center justify-content-between">
        {delivery.deliveredAt ? (
          <QuotePill tone="green">
            {t("Delivered on {{date}}", { date: day(delivery.deliveredAt) })}
          </QuotePill>
        ) : (
          <span />
        )}
        {!closed && (
          <Button
            loading={saving}
            onClick={() =>
              confirm(
                t("{{name}}'s project will be completed. Mark the order as delivered", {
                  name: customer,
                }),
                markSaleDelivered(sale.id, projectId, t),
              )
            }
          >
            {t("Mark as delivered")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProjectDelivery;
